import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';
import { Authority } from './components/AuthorityLabel';
import { Loading } from './Loading';
import './stylesheets/Navigator.css';

/**
 * 表示中のパスがメニューの項目の配下にあるかを、パスの区切りの単位で前方一致して判定する。
 * 「/configs/frames/1/edit」は「/configs/frames」の配下とし、「/configs/frames_old」は配下としない。
 */
function isActivePath(path: string, itemPath: string): boolean {
  if (path === itemPath) {
    return true;
  }
  return path.startsWith(itemPath + '/');
}

function getLinkClassName(active: boolean): string {
  if (active) {
    return 'Navigator-link Navigator-selected';
  }
  return 'Navigator-link';
}

/**
 * メニューの項目のアイコン。項目ごとに別のアイコンにして、アイコンだけでも見分けられるようにする。
 * 物件一覧は standard のメニューの画像、それ以外はアプリ（eve-unity）に入っている線画アイコン（CleanFlatIcon）を黒くして使う。
 * 図枠設定はアプリの設定メニューの「図枠設定」と同じアイコン（icon_line_media_75）
 */
const ICON_IMAGES = {
  places: { src: '/building.svg', alt: 'Places' },
  tiles: { src: '/menu/tile.png', alt: 'Tiles' },
  frames: { src: '/menu/frame.png', alt: 'Frames' },
  accounts: { src: '/menu/accounts.png', alt: 'Users' },
  clients: { src: '/menu/clients.png', alt: 'Clients' },
  devices: { src: '/menu/devices.png', alt: 'Devices' },
  deviceLimit: { src: '/menu/device_limit.png', alt: 'Device limit' },
};

type NavigatorIconName = keyof typeof ICON_IMAGES;

const NavigatorIcon: React.FC<{ name: NavigatorIconName }> = function (props) {
  const image = ICON_IMAGES[props.name];
  return (
    <span className="Navigator-link_icon">
      <img alt={image.alt} src={image.src} />
    </span>
  );
};

interface NavigatorLinkProps {
  to: string;
  label: string;
  /** 表示中のパス */
  path: string;
  icon: NavigatorIconName;
}

const NavigatorLink: React.FC<NavigatorLinkProps> = function (props) {
  const active = isActivePath(props.path, props.to);

  let ariaCurrent: 'page' | undefined;
  if (active) {
    ariaCurrent = 'page';
  }

  return (
    <li className="Navigator-item">
      <Link to={props.to} className={getLinkClassName(active)} aria-current={ariaCurrent}>
        <NavigatorIcon name={props.icon} />
        <span className="Navigator-link_text">{props.label}</span>
      </Link>
    </li>
  );
};

/**
 * 項目をまとめる見出し。見出し自体は押せず、アイコンも付けない小さな灰色の文字にして、アイコン付きの項目と見分けられるようにする
 */
const NavigatorHeading: React.FC<{ title: string }> = function (props) {
  return (
    <li className="Navigator-heading" role="presentation">
      {props.title}
    </li>
  );
};

/**
 * アプリケーションのナビゲーション (メニュー)
 * 項目の数が少ないので、すべての項目を常に表示する。見出しごとの表示条件は、IndexPage.tsxの各画面に付けた権限の確認と同じ段階にする。
 */
export const Navigator: React.FC = function () {
  const path = useLocation().pathname;
  const appContext = React.useContext(AppContext);

  const me = appContext.state.me;

  if (!me) {
    return <Loading />;
  }

  const level = me.authority.level;

  let masterItems;
  if (level >= Authority.MasterAdmin) {
    masterItems = (
      <>
        <NavigatorHeading title="共通設定" />
        <NavigatorLink to="/configs/shape_tiles" label="タイル設定" path={path} icon="tiles" />
        <NavigatorLink to="/configs/frames" label="図枠設定" path={path} icon="frames" />
      </>
    );
  }

  let superAdminLink;
  if (level >= Authority.SuperAdmin) {
    superAdminLink = <NavigatorLink to="/superadmin" label="制限台数設定" path={path} icon="deviceLimit" />;
  }

  let adminItems;
  if (level >= Authority.Admin) {
    adminItems = (
      <>
        <NavigatorHeading title="管理画面" />
        <NavigatorLink to="/admin/accounts" label="ユーザー一覧" path={path} icon="accounts" />
        <NavigatorLink to="/admin/clients" label="お客様一覧" path={path} icon="clients" />
        <NavigatorLink to="/admin/devices" label="台数使用状況" path={path} icon="devices" />
        {superAdminLink}
      </>
    );
  }

  return (
    <div className="Navigator-container">
      <nav aria-label="メニュー">
        <ul className="Navigator-list">
          <NavigatorLink to="/places" label="物件一覧" path={path} icon="places" />
          {masterItems}
          {adminItems}
        </ul>
      </nav>
    </div>
  );
};
